<script setup>
  import { defineProps, defineEmits, computed, nextTick, ref, watch } from 'vue'
  import { activeSentence, formatTranscriptTime, sentenceRange } from './transcriptTime'

  const props = defineProps({
    items: { type: Array, default: () => [] },
    playback: { type: Object, required: true },
    positionMs: { type: Number, default: 0 },
    following: Boolean,
  })
  const emit = defineEmits(['seek', 'browse'])
  const list = ref(null)
  const activeId = computed(() => activeSentence(props.items, props.positionMs, props.playback))
  const scrollToActive = async () => {
    await nextTick()
    const el = list.value?.querySelector('[aria-current="true"]')
    if (el && list.value) list.value.scrollTop = el.offsetTop - list.value.clientHeight / 3
  }
  watch([activeId, () => props.following, () => props.items], () => {
    if (props.following) scrollToActive()
  })
  const seek = (item) => emit('seek', item, sentenceRange(item, props.playback))
</script>

<template>
  <div
    ref="list"
    class="transcript-list"
    tabindex="0"
    @keydown="emit('browse')"
    @pointerdown="emit('browse')"
    @touchmove.passive="emit('browse')"
    @wheel.passive="emit('browse')"
  >
    <article
      v-for="item in items"
      :key="item.id"
      :aria-current="item.id === activeId ? 'true' : 'false'"
      class="sentence"
      :class="{ active: item.id === activeId }"
    >
      <div class="sentence-meta">
        <button class="timestamp" :disabled="!sentenceRange(item, playback)" type="button" @click="seek(item)">
          {{ formatTranscriptTime(sentenceRange(item, playback)?.start) }}
        </button>
        <span>{{ item.speakerId ? `说话人 ${item.speakerId}` : '说话人未标记' }}</span>
        <span v-if="item.scheduleCode" class="schedule">{{ item.scheduleCode }}</span>
      </div>
      <button class="sentence-text" :disabled="!sentenceRange(item, playback)" type="button" @click="seek(item)">
        {{ item.text }}
      </button>
    </article>
  </div>
</template>

<style scoped>
  .transcript-list {
    position: relative;
    flex: 1;
    min-height: 100px;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px 24px;
  }
  .sentence {
    padding: 22px 20px;
    margin-bottom: 4px;
    border-left: 3px solid transparent;
    border-radius: 3px;
  }
  .sentence.active {
    background: #edf5ff;
    border-left-color: #409eff;
  }
  .sentence-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 10px;
    color: #909399;
    font-size: 12px;
  }
  .timestamp {
    padding: 0;
    border: 0;
    background: transparent;
    font-variant-numeric: tabular-nums;
    color: #409eff;
    cursor: pointer;
  }
  .timestamp:disabled {
    color: #909399;
    cursor: default;
  }
  .schedule {
    margin-left: auto;
  }
  .sentence-text {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: #303133;
    font: inherit;
    font-size: 16px;
    line-height: 1.9;
    text-align: left;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    cursor: pointer;
  }
  .sentence-text:disabled {
    color: #303133;
    cursor: text;
  }
  @media (max-width: 768px) {
    .transcript-list {
      flex: none;
      height: 50vh;
      padding: 0;
    }
    .sentence {
      padding: 18px 12px;
    }
  }
</style>
